if (!customElements.get("variant-gallery")) {
  const WIDTHS = [480, 740, 980, 1200, 1600, 1920, 2560];
  const THUMB_WIDTHS = [65, 130];
  const MIN_ASPECT_RATIO = 0.6;

  const escapeAttr = (str) =>
    String(str == null ? "" : str)
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

  class VariantGallery extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.mainSlider = this.querySelector(".mto-gallery__main");
      this.thumbSlider = this.querySelector(".mto-gallery__thumbs");
      this.mainGrid = this.querySelector(".js-mto-main-grid");
      this.thumbGrid = this.querySelector(".js-mto-thumb-grid");
      this.zoomEnabled = this.hasAttribute("data-zoom-enabled");
      this.zoomSize = parseInt(this.dataset.zoomSize, 10) || 5000;
      this.zoomLabel = this.dataset.zoomLabel || "";

      try {
        this.data = JSON.parse(
          this.querySelector(".js-mto-gallery-data").textContent
        );
      } catch (e) {
        this.data = {};
      }

      this.currentImages = this.readImagesFromDom();

      if (this.zoomEnabled) this.setupZoom();

      theme.addDelegateEventListener(
        this,
        "click",
        ".thumbnail",
        this.handleThumbnailClick.bind(this)
      );

      this.mainSlider.addEventListener(
        "on:carousel-slider:select",
        this.handleSlideSelect.bind(this)
      );

      const productEl = this.closest(".js-product");
      if (productEl) {
        productEl.addEventListener(
          "on:variant:change",
          this.handleVariantChange.bind(this)
        );
      }
    }

    readImagesFromDom() {
      return [...this.mainGrid.querySelectorAll(".slider__item img")].map(
        (img) => ({
          u: img.src.split(/[?&]width=/)[0],
          w: parseInt(img.getAttribute("width"), 10) || 0,
          h: parseInt(img.getAttribute("height"), 10) || 0,
          a: img.alt || "",
        })
      );
    }

    static sized(url, width) {
      return `${url}${url.includes("?") ? "&" : "?"}width=${width}`;
    }

    static srcset(image, widths) {
      const usable = widths.filter((w) => !image.w || w <= image.w);
      if (usable.length === 0) usable.push(image.w || widths[0]);
      return usable
        .map((w) => `${VariantGallery.sized(image.u, w)} ${w}w`)
        .join(", ");
    }

    static aspectRatio(image) {
      if (!image.w || !image.h) return 1;
      return Math.max(image.w / image.h, MIN_ASPECT_RATIO);
    }

    mainItemMarkup(image, index, total) {
      const ratio = VariantGallery.aspectRatio(image);
      const img = `<img class="theme-img" src="${VariantGallery.sized(
        image.u,
        1200
      )}" srcset="${VariantGallery.srcset(image, WIDTHS)}" sizes="(min-width: 768px) 50vw, 100vw"${
        image.w ? ` width="${image.w}"` : ""
      }${image.h ? ` height="${image.h}"` : ""} alt="${escapeAttr(
        image.a
      )}" loading="${index === 0 ? "eager" : "lazy"}">`;

      const media = `<div class="product-media-wrapper" data-media-id="mto-${index}" tabindex="-1"><div class="product-media product-media--image"><div class="img-ar img-ar--contain" style="--aspect-ratio: ${ratio}">${img}</div></div></div>`;

      const inner = this.zoomEnabled
        ? `<a class="block show-gallery" href="${VariantGallery.sized(
            image.u,
            this.zoomSize
          )}" aria-label="${escapeAttr(
            this.zoomLabel.replace("[index]", index + 1)
          )}">${media}</a>`
        : media;

      return `<div class="slider__item${
        index === 0 ? " is-active" : ""
      }" role="group" aria-roledescription="slide" aria-label="${
        index + 1
      } of ${total}"${
        index === 0 ? "" : ' tabindex="-1"'
      } data-media-id="mto-${index}">${inner}</div>`;
    }

    thumbItemMarkup(image, index) {
      const ratio = VariantGallery.aspectRatio(image);
      const href = this.zoomEnabled
        ? VariantGallery.sized(image.u, this.zoomSize)
        : "#";

      return `<li class="slider__item${
        index === 0 ? " is-active" : ""
      }" data-media-id="mto-${index}"><a class="thumbnail thumbnail--media-image" href="${href}"><div class="img-ar img-ar--contain" style="--aspect-ratio: ${ratio}"><img class="theme-img" src="${VariantGallery.sized(
        image.u,
        130
      )}" srcset="${VariantGallery.srcset(
        image,
        THUMB_WIDTHS
      )}" sizes="65px" alt="" loading="lazy"></div></a></li>`;
    }

    handleVariantChange(evt) {
      const variant = evt.detail.variant;
      const images = variant ? this.data[variant.id] : null;

      if (!images || images.length === 0) return;

      this.render(images);
    }

    render(images) {
      this.currentImages = images;

      this.mainGrid.innerHTML = images
        .map((image, i) => this.mainItemMarkup(image, i, images.length))
        .join("");
      this.thumbGrid.innerHTML = images
        .map((image, i) => this.thumbItemMarkup(image, i))
        .join("");

      const multiple = images.length > 1;
      this.thumbSlider.hidden = !multiple;
      const nav = this.mainSlider.querySelector(".slider-nav");
      if (nav) nav.hidden = !multiple;

      const scroller = this.mainSlider.querySelector(".slider");
      if (scroller) scroller.scrollLeft = 0;

      this.mainSlider.refresh();
      if (multiple) this.thumbSlider.refresh();
    }

    setupZoom() {
      theme.addDelegateEventListener(
        this,
        "click",
        ".show-gallery",
        this.openLightbox.bind(this)
      );
    }

    openLightbox(evt, imageLink) {
      evt.preventDefault();

      if (!customElements.get("mto-lightbox")) return;

      if (!this.lightbox) {
        this.lightbox = document.createElement("mto-lightbox");
        document.body.appendChild(this.lightbox);
      }

      const item = imageLink.closest(".slider__item");
      const index = item ? [...this.mainGrid.children].indexOf(item) : 0;

      this.lightbox.open(this.currentImages, Math.max(index, 0));
    }

    handleThumbnailClick(evt, thumbnail) {
      evt.preventDefault();
      this.setActiveMedia(thumbnail.parentNode.dataset.mediaId, true);
    }

    handleSlideSelect(evt) {
      if (evt.detail.slide && evt.detail.slide.dataset.mediaId) {
        this.setActiveMedia(evt.detail.slide.dataset.mediaId, false);
      }
    }

    setActiveMedia(mediaId, scrollToItem) {
      this.querySelectorAll(`.slider__item`).forEach((el) => {
        el.classList.toggle("is-active", el.dataset.mediaId === mediaId);
      });

      if (!scrollToItem) return;

      const item = this.mainGrid.querySelector(
        `.slider__item[data-media-id="${mediaId}"]`
      );
      const scroller = this.mainSlider.querySelector(".slider");
      if (item && scroller) scroller.scrollLeft = item.offsetLeft;
    }
  }

  customElements.define("variant-gallery", VariantGallery);
}
